import { translate } from './translations';
import {
    DEFAULT_CONFIG,
    MONOCHROME_COLORS,
    NAUTICA_MILE_TO_KMH,
    HORA_INICI_DIA,
    HORA_FI_DIA,
    SUFFIXES,
    WIND_SPEED_COLORS,
    TENDENCIES_VALUES,
} from './globals';
import * as Icons from '../img';
import React from 'react';

export const dateParser = date => {
    const dateData = {};
    var year = date.getFullYear() + '';
    var month = date.getMonth() + 1 + '';
    var day = date.getDate() + '';

    day = day < 10 ? `0${day}` : day;
    month = month < 10 ? `0${month}` : month;

    dateData.day = day;
    dateData.month = month;
    dateData.year = year;

    return dateData;
};

// format date in views
export const formatDateUi = date => {
    const dateFormatted = dateParser(date);
    const { day, month, year } = dateFormatted;
    return `${day}.${month}.${year}`;
};

// format date for api calls in utils/api.js
export const formatDate = date => {
    const dateFormatted = dateParser(date);
    const { day, month, year } = dateFormatted;
    return `${year}${month}${day}`;
};

export const getLocationData = (locationCode, data) => {
    let municipiData = data.municipis.find(value => {
        return value.codi === locationCode;
    });

    if (municipiData) return municipiData.valors;
};

export const getUnitData = data => data.unitat;

export const buildLanguageConfig = ({
    language = DEFAULT_CONFIG.language,
    color = DEFAULT_CONFIG.color,
    target = DEFAULT_CONFIG.target,
}) => {
    color = `#${color}`;
    if (validateColor(color) === false) color = DEFAULT_CONFIG.color;
    if (target !== '_parent' && target !== '_blank') target = '_parent';
    return { language, color, target };
};

export const formatDateToDayName = (date, language, format) => {
    const newDate = new Date(date);
    const dayOfWeek = newDate.getDay();

    return translate(language, `days_of_week_${format}_${dayOfWeek}`);
};

export const dayAfterTomorrow = (language, format) => {
    let today = new Date();
    let dayAfterTomorrow = today.setDate(today.getDate() + 2);

    return formatDateToDayName(dayAfterTomorrow, language, format);
};

export const getLocationName = (locationCode, cities) => {
    if (cities.length > 0) {
        const town = cities.find(town => town.codi === locationCode);
        if (town) return town.nom;
    }
};

export const getEstCel = valor => {
    return Icons[`est_cel_${valor}`];
};

export const getEstCelAmbLlunes = (valor, hora) => {
    if (hora < HORA_INICI_DIA || hora > HORA_FI_DIA) {
        return Icons[`est_cel_${valor}n`];
    }
    return Icons[`est_cel_${valor}`];
};

export const getTendencyIcon = tendencia => {
    const { tipus, valor } = tendencia;
    const value = TENDENCIES_VALUES[valor].toLowerCase();
    return Icons[`temp_${tipus}_${value}`];
};

export const sleep = ms => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const debounce = (func, wait = 20, immediate = true) => {
    var timeout;
    return function() {
        const context = this,
            args = arguments;
        const later = function() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
};

export const hexToRgba = (hex, a) => {
    const hexTrimmed = hex.replace('#', '');
    var bigint = parseInt(hexTrimmed, 16);
    var r = (bigint >> 16) & 255;
    var g = (bigint >> 8) & 255;
    var b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const tempMaxColor = color => {
    const tempColor =
        color === DEFAULT_CONFIG.color
            ? DEFAULT_CONFIG.tempMaxColor
            : hexToRgba(color, MONOCHROME_COLORS.tempMaxAlpha);

    return tempColor;
};

export const tempMinColor = color => {
    const tempColor =
        color === DEFAULT_CONFIG.color
            ? DEFAULT_CONFIG.tempMinColor
            : hexToRgba(color, MONOCHROME_COLORS.tempMinAlpha);

    return tempColor;
};

export const headerColors = color => {
    const { headerColor1, headerColor2, headerColor3 } = DEFAULT_CONFIG;
    const { tempMinAlpha, tempMaxAlpha } = MONOCHROME_COLORS;

    return color === DEFAULT_CONFIG.color || color === '#'
        ? {
              colors: {
                  color1: headerColor1,
                  color2: headerColor2,
                  color3: headerColor3,
              },
              alphas: { alpha1: '1', alpha2: '1', alpha3: '1' },
          }
        : {
              colors: { color1: color, color2: color, color3: color },
              alphas: {
                  alpha1: '1',
                  alpha2: tempMinAlpha,
                  alpha3: tempMaxAlpha,
              },
          };
};

export const dayNavColor = color => {
    const colorNav =
        color !== DEFAULT_CONFIG.color
            ? color
            : DEFAULT_CONFIG.dayNavCurrentColor;

    return colorNav;
};

export const removeAccents = text =>
    text.normalize('NFD').replace(/[\u0300\u0301­\u0308]/g, '');

export const celsiusToFahrenheit = celsius => {
    const fahrenheit = Math.round(celsius * 1.8 + 32);
    return fahrenheit;
};

export const kmToKnots = km => {
    const knots = Math.round(km / NAUTICA_MILE_TO_KMH);
    return knots;
};

/**
 * @param {number} speed The actual speed
 * @param {string} format 'color' or 'svgIcon'
 * @param {string} windSpeedFormat String representing Km/h or knots
 * @return {string} A color or a svg icon file name.
 */
export const getArrowColor = (
    speed,
    format = 'color',
    windSpeedFormat = SUFFIXES.speed.km
) => {
    let index = 0;

    if (windSpeedFormat === SUFFIXES.speed.km) {
        switch (true) {
            case speed < 5:
                index = 0;
                break;
            case speed < 10:
                index = 5;
                break;
            case speed < 20:
                index = 10;
                break;
            case speed < 30:
                index = 20;
                break;
            case speed < 40:
                index = 30;
                break;
            case speed < 50:
                index = 40;
                break;
            case speed < 60:
                index = 50;
                break;
            case speed < 70:
                index = 60;
                break;
            case speed < 80:
                index = 70;
                break;
            case speed >= 80:
                index = 80;
                break;
            default:
                index = 0;
                break;
        }
    } else {
        switch (true) {
            case speed < 3:
                index = 0;
                break;
            case speed < 5:
                index = 5;
                break;
            case speed < 10:
                index = 10;
                break;
            case speed < 15:
                index = 20;
                break;
            case speed < 20:
                index = 30;
                break;
            case speed < 25:
                index = 40;
                break;
            case speed < 30:
                index = 50;
                break;
            case speed < 35:
                index = 60;
                break;
            case speed < 40:
                index = 70;
                break;
            case speed >= 40:
                index = 80;
                break;
            default:
                index = 0;
                break;
        }
    }

    return format === 'color'
        ? WIND_SPEED_COLORS[index]
        : Icons[`vent_direccio_${index}`];
};

export const renderitzarComponent = (nomComponent, props) => {
    const { children, ...propietats } = props;
    return (
        <code nom={nomComponent} props={propietats}>
            {children}
        </code>
    );
};

export const hasItTooMuchLuminosity = colorString => {
    const color = colorString.substr(1);
    let rgb = '';

    if (color.length === 6) {
        rgb = color.match(/(\S{2})/g);
    }

    if (color.length === 3) {
        const colorParts = color.match(/(\S{1})/g);
        rgb = colorParts.map(x => (x = `${x}${x}`));
    }

    if (rgb) {
        var r = parseInt(rgb[0], 16);
        var g = parseInt(rgb[1], 16);
        var b = parseInt(rgb[2], 16);
    }

    const luminosity = (r * 299 + g * 587 + b * 114) / 1000;

    if (luminosity > 255 * 0.7) return true;
    return false;
};

export const validateColor = colorString => {
    if (hasItTooMuchLuminosity(colorString)) return false;
    return /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(colorString);
};

export const consoleLogError = (error, language) => {
    console.error(translate(language, 'error_message'), error.message);
};

export const convertToTwoDigitFigures = x => {
    return x < 10 ? `0${x}` : x;
};



// WEBPACK FOOTER //
// ./src/utils/utils.js